// Vitality Score — postmenopause 0–100 recovery-state number.
//
// Reads the same wearable_metrics rows the Symptom-Risk Forecast uses, but
// scores RECOVERY (how well-recovered you are today vs your own baseline)
// rather than symptom risk. Phase 1 signals: sleep duration, HRV (RMSSD),
// resting heart rate. Sleep quality is intentionally NOT folded in here yet
// (Option A — duration only); it will join in a later phase.
//
// IMPORTANT: this file does NOT modify symptomRiskForecast.ts or menoScore.ts.
// It reuses BASELINE_WINDOW_NIGHTS / MIN_BASELINE_NIGHTS from the forecast
// module so the "personal baseline" definition stays consistent.

import { supabase } from "@/integrations/supabase/client";
import {
  fetchWearableMetrics,
  type WearableMetricRow,
} from "./wearableMetrics";
import {
  BASELINE_WINDOW_NIGHTS,
  MIN_BASELINE_NIGHTS,
} from "./symptomRiskForecast";
// Bands are the SINGLE SOURCE shared with the Menopause Score so Vitality
// bands can never drift. Do NOT redeclare cutoffs/colors here.
import { bandFor as menoBandFor, type Band } from "./menoScore";

// ---------- Tunable constants ----------
// One "sensitivity unit" of deviation in each signal. The per-signal
// sub-score is `clamp(75 + UNIT_SHIFT * dev_units, 25, 100)`, where
// `dev_units` is always expressed in the same unit space across signals so
// the clamp behaves identically for each. Adjust these to retune behaviour
// without touching the scoring math.
export const HRV_SENSITIVITY_PCT = 10;   // 10% change in HRV vs baseline = 1 unit
export const RHR_SENSITIVITY_BPM = 3;    // 3 bpm change in resting HR     = 1 unit
export const SLEEP_SENSITIVITY_MIN = 45; // 45 min change in sleep duration = 1 unit

// Centre of the score (a "normal vs baseline" day) and how much one
// sensitivity unit moves the sub-score. UNIT_SHIFT=25 means +1 unit → 100,
// -1 unit → 50, ±2 units saturates the clamp.
const BASELINE_SUBSCORE = 75;
const UNIT_SHIFT = 25;
const SUBSCORE_MIN = 25;
const SUBSCORE_MAX = 100;

// Re-export of the shared band lookup so callers don't have to know which
// module owns the band table. menoScore.bandFor returns the "unscored"
// sentinel for null; vitalityBandFor only ever runs on a finite score.
export type VitalityBand = Band;
export function vitalityBandFor(score: number): VitalityBand {
  return menoBandFor(score);
}

export type VitalityStatus = "ok" | "building_baseline" | "no_data";
export type VitalitySignalKey = "hrv" | "resting_hr" | "sleep";
export type VitalityDirection = "better" | "worse" | "near";

export type VitalityContribution = {
  key: VitalitySignalKey;
  label: string;
  lastNight: number;
  baseline: number;
  unit: string;
  subScore: number;            // 25..100, 75 = on-baseline
  direction: VitalityDirection;
  detail: string;              // human-readable, e.g. "HRV 48ms vs your 52ms baseline (−8%)"
};

export type VitalityScore = {
  status: VitalityStatus;
  score: number | null;          // 0..100
  band: VitalityBand | null;
  lead: string;                  // one-line headline copy
  contributions: VitalityContribution[]; // sorted by |subScore-75| desc
  signalsUsed: number;           // how many of 3 signals contributed
  baselineNights: number;        // baseline rows available (max across signals)
  asOfDate: string | null;       // metric_date of last-night row
};

// ---------- helpers ----------
function median(values: number[]): number {
  const s = [...values].sort((a, b) => a - b);
  const n = s.length;
  if (n === 0) return NaN;
  const mid = Math.floor(n / 2);
  return n % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function baselineValues(rows: WearableMetricRow[], field: keyof WearableMetricRow): number[] {
  const out: number[] = [];
  for (const r of rows) {
    const v = num(r[field] as unknown);
    if (v !== null) out.push(v);
  }
  return out;
}

function clampSub(raw: number): number {
  if (!Number.isFinite(raw)) return BASELINE_SUBSCORE;
  return Math.max(SUBSCORE_MIN, Math.min(SUBSCORE_MAX, Math.round(raw)));
}

function directionFor(subScore: number): VitalityDirection {
  if (subScore >= BASELINE_SUBSCORE + 5) return "better";
  if (subScore <= BASELINE_SUBSCORE - 5) return "worse";
  return "near";
}

function signed(n: number, digits = 0): string {
  const v = digits ? n.toFixed(digits) : Math.round(n).toString();
  return n > 0 ? `+${v}` : v;
}

// ---------- core ----------
export function computeVitalityScore(rows: WearableMetricRow[]): VitalityScore {
  const empty: VitalityScore = {
    status: "no_data",
    score: null,
    band: null,
    lead: "No wearable data yet — connect a device to start your Vitality Score.",
    contributions: [],
    signalsUsed: 0,
    baselineNights: 0,
    asOfDate: null,
  };
  if (!rows || rows.length === 0) return empty;

  const sorted = [...rows].sort((a, b) => a.metric_date.localeCompare(b.metric_date));
  const lastNight = sorted[sorted.length - 1];
  const priorAll = sorted.slice(0, -1);
  const priorWindow = priorAll.slice(Math.max(0, priorAll.length - BASELINE_WINDOW_NIGHTS));

  const hrvBaseVals = baselineValues(priorWindow, "hrv_ms");
  const rhrBaseVals = baselineValues(priorWindow, "resting_heart_rate");
  const sleepBaseVals = baselineValues(priorWindow, "sleep_duration_minutes");

  const hrvLast = num(lastNight.hrv_ms);
  const rhrLast = num(lastNight.resting_heart_rate);
  const sleepLast = num(lastNight.sleep_duration_minutes);

  const contributions: VitalityContribution[] = [];

  // HRV — higher = better recovered. dev_units = (Δ/baseline) / (SENS_PCT/100)
  if (hrvLast !== null && hrvBaseVals.length >= MIN_BASELINE_NIGHTS) {
    const base = median(hrvBaseVals);
    if (base > 0) {
      const fracChange = (hrvLast - base) / base;            // e.g. -0.08
      const devUnits = fracChange / (HRV_SENSITIVITY_PCT / 100);
      const sub = clampSub(BASELINE_SUBSCORE + UNIT_SHIFT * devUnits);
      const pct = Math.round(fracChange * 100);
      contributions.push({
        key: "hrv",
        label: "HRV",
        lastNight: hrvLast,
        baseline: base,
        unit: "ms",
        subScore: sub,
        direction: directionFor(sub),
        detail: `${Math.round(hrvLast)}ms vs ${Math.round(base)}ms baseline (${signed(pct)}%)`,
      });
    }
  }

  // Resting HR — lower = better recovered. dev_units = (baseline - last) / SENS_BPM
  if (rhrLast !== null && rhrBaseVals.length >= MIN_BASELINE_NIGHTS) {
    const base = median(rhrBaseVals);
    const bpmDeltaBetter = base - rhrLast;                   // positive = better
    const devUnits = bpmDeltaBetter / RHR_SENSITIVITY_BPM;
    const sub = clampSub(BASELINE_SUBSCORE + UNIT_SHIFT * devUnits);
    contributions.push({
      key: "resting_hr",
      label: "Resting HR",
      lastNight: rhrLast,
      baseline: base,
      unit: "bpm",
      subScore: sub,
      direction: directionFor(sub),
      detail: `${Math.round(rhrLast)}bpm vs ${Math.round(base)}bpm baseline (${signed(rhrLast - base)} bpm)`,
    });
  }

  // Sleep duration — more = better recovered. dev_units = (last - baseline) / SENS_MIN
  if (sleepLast !== null && sleepBaseVals.length >= MIN_BASELINE_NIGHTS) {
    const base = median(sleepBaseVals);
    const minDelta = sleepLast - base;                        // positive = better
    const devUnits = minDelta / SLEEP_SENSITIVITY_MIN;
    const sub = clampSub(BASELINE_SUBSCORE + UNIT_SHIFT * devUnits);
    contributions.push({
      key: "sleep",
      label: "Sleep duration",
      lastNight: sleepLast,
      baseline: base,
      unit: "min",
      subScore: sub,
      direction: directionFor(sub),
      detail: `${Math.round(sleepLast)}min vs ${Math.round(base)}min baseline (${signed(minDelta)} min)`,
    });
  }

  const baselineNights = Math.max(hrvBaseVals.length, rhrBaseVals.length, sleepBaseVals.length);

  // Gate: need at least 2 of 3 signals with usable baseline + last-night.
  if (contributions.length < 2) {
    return {
      status: "building_baseline",
      score: null,
      band: null,
      lead:
        baselineNights === 0
          ? "Your Vitality Score is calibrating — log a few nights of wearable data to begin."
          : `Building your baseline — ${baselineNights}/${MIN_BASELINE_NIGHTS} nights so far.`,
      contributions: [],
      signalsUsed: contributions.length,
      baselineNights,
      asOfDate: lastNight.metric_date,
    };
  }

  // Equal-weight average of available sub-scores.
  const avg = contributions.reduce((s, c) => s + c.subScore, 0) / contributions.length;
  const score = Math.max(0, Math.min(100, Math.round(avg)));
  const band = vitalityBandFor(score);

  const sortedContribs = [...contributions].sort(
    (a, b) => Math.abs(b.subScore - BASELINE_SUBSCORE) - Math.abs(a.subScore - BASELINE_SUBSCORE),
  );

  const lead = leadCopyFor(score, band, sortedContribs);

  return {
    status: "ok",
    score,
    band,
    lead,
    contributions: sortedContribs,
    signalsUsed: contributions.length,
    baselineNights,
    asOfDate: lastNight.metric_date,
  };
}

function driverPhrase(top: VitalityContribution): string {
  const delta = top.lastNight - top.baseline;
  if (Math.abs(delta) < 0.5) return `${top.label.toLowerCase()} near your baseline`;
  const word = delta > 0 ? "above" : "below";
  return `${top.label.toLowerCase()} ${word} your baseline`;
}

function leadCopyFor(
  score: number,
  _band: VitalityBand,
  contribs: VitalityContribution[],
): string {
  const top = contribs[0];
  const driver = top ? driverPhrase(top) : "your overnight signals";
  if (score >= 85) return `Well recovered — driven by ${driver}.`;
  if (score >= 70) return `Solid recovery — ${driver}.`;
  if (score >= 50) return `Mixed recovery — ${driver}.`;
  if (score >= 30) return `Under-recovered — ${driver}.`;
  return `Low recovery — ${driver}. Treat today as a lighter day.`;
}

// ---------- loader ----------
export async function loadVitalityScore(userId: string, days = 30): Promise<VitalityScore> {
  const rows = await fetchWearableMetrics(userId, days);
  return computeVitalityScore(rows);
}

// Convenience: resolve the current user and compute.
export async function loadVitalityScoreForCurrentUser(): Promise<VitalityScore | null> {
  const { data } = await supabase.auth.getUser();
  const uid = data?.user?.id;
  if (!uid) return null;
  return loadVitalityScore(uid);
}

// ---------- series (for the Vitality detail "Last 14 days" chart) ----------
//
// Reuses computeVitalityScore as the per-day kernel — no parallel math. For
// each of the last `days` calendar dates ending at the latest available row,
// finds the row for that date and computes the score from rows up to and
// including it. Days with no row, or where the per-day result is
// building_baseline / no_data, are emitted as score=null. That gives the
// chart honest gaps to draw across instead of fabricated values.
export type VitalityScoreSeriesPoint = { dateKey: string; score: number | null };

function addDaysKey(anchorKey: string, deltaDays: number): string {
  const d = new Date(anchorKey + "T00:00:00");
  d.setDate(d.getDate() + deltaDays);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function computeVitalityScoreSeries(
  rows: WearableMetricRow[],
  days = 14,
): VitalityScoreSeriesPoint[] {
  if (!rows || rows.length === 0) return [];
  const sorted = [...rows].sort((a, b) => a.metric_date.localeCompare(b.metric_date));
  const byDate = new Map<string, WearableMetricRow>();
  for (const r of sorted) byDate.set(r.metric_date, r);
  const anchorKey = sorted[sorted.length - 1].metric_date;
  const out: VitalityScoreSeriesPoint[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const dk = addDaysKey(anchorKey, -i);
    const row = byDate.get(dk);
    if (!row) { out.push({ dateKey: dk, score: null }); continue; }
    const upto: WearableMetricRow[] = [];
    for (const r of sorted) {
      if (r.metric_date <= dk) upto.push(r);
    }
    const v = computeVitalityScore(upto);
    out.push({ dateKey: dk, score: v.status === "ok" ? v.score : null });
  }
  return out;
}

// Loader: fetch enough days that the OLDEST point in a `days`-length series
// still has BASELINE_WINDOW_NIGHTS of prior rows available for baseline.
export async function loadVitalityScoreSeriesForCurrentUser(
  days = 14,
): Promise<VitalityScoreSeriesPoint[]> {
  const { data } = await supabase.auth.getUser();
  const uid = data?.user?.id;
  if (!uid) return [];
  const rows = await fetchWearableMetrics(uid, days + BASELINE_WINDOW_NIGHTS + 2);
  return computeVitalityScoreSeries(rows, days);
}
